import {Progress} from 'antd';
import React from 'react';
import {createPortal} from 'react-dom';

import * as localizationKeys from '../../../consts/localization/localizationKeys';
import {useTooltip} from '../../../hooks/useTooltip';
import {getLocalizedStrings} from '../../../services/localization/localizationService';
import numberConverter from '../../../utils/numberConverter';

const toPercentage = (value) => {
    const percentage = Number(value);

    return Number.isFinite(percentage) ? Math.min(100, Math.max(0, percentage)) : 0;
};

const DrawerSectionCoverageProgress = ({
    averageStatusPercentage,
    successPercentage,
    failurePercentage = 0,
    mobile = false,
}) => {
    const localizedStrings = getLocalizedStrings();

    const success = mobile ? toPercentage(successPercentage) : successPercentage;
    const failure = mobile ? Math.min(toPercentage(failurePercentage), 100 - success) : failurePercentage;
    const average = mobile ? success + failure : averageStatusPercentage;
    const formatPercentage = (value) =>
        mobile ? Math.round(value * 10) / 10 : numberConverter.formatPercent(value, 1);
    const roundedSuccess = formatPercentage(success);
    const roundedFailure = formatPercentage(failure);
    const roundedAverage = formatPercentage(average);

    const absent = formatPercentage(100 - average);

    const {containerRef, isVisible, coords, handleMouseEnter, handleMouseLeave, handleClick} = useTooltip(!mobile);
    const legend = [
        {
            key: 'success',
            label: localizationKeys.MAP_BUILDING_COVERAGE_SUCCESS,
            value: roundedSuccess,
            color: 'var(--color-marker-green)',
        },
        {
            key: 'failure',
            label: localizationKeys.MAP_BUILDING_COVERAGE_FAILURE,
            value: roundedFailure,
            color: 'var(--color-flamingo)',
        },
        {key: 'absent', label: localizationKeys.MAP_BUILDING_COVERAGE_ABSENT, value: absent, color: '#E6E6E6'},
    ];

    return (
        <section className={mobile ? 'map-building-mobile-coverage' : undefined}>
            <div className={'flex justify-between items-end px-1'}>
                {mobile ? (
                    <h3>{localizedStrings[localizationKeys.DATA_COVERAGE_TEXT]}</h3>
                ) : (
                    <div className={'text-16 font-bold text-zambezi'}>
                        {localizedStrings[localizationKeys.DATA_COVERAGE_TEXT]}
                    </div>
                )}
                <div className={'text-14 font-bold text-zambezi'}>{mobile ? roundedAverage : roundedSuccess}%</div>
            </div>

            <div
                className={mobile ? 'relative w-full' : 'cursor-help relative w-full'}
                ref={containerRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onClick={handleClick}
            >
                <Progress
                    aria-label={mobile ? localizedStrings[localizationKeys.DATA_COVERAGE_TEXT] : undefined}
                    aria-valuenow={mobile ? roundedAverage : undefined}
                    aria-valuemin={mobile ? 0 : undefined}
                    aria-valuemax={mobile ? 100 : undefined}
                    className={'mb-0'}
                    percent={roundedAverage}
                    success={{
                        percent: roundedSuccess,
                        strokeColor: 'var(--color-marker-green)',
                    }}
                    strokeColor={'var(--color-flamingo)'}
                    showInfo={false}
                    trailColor={'#E6E6E6'}
                />

                {isVisible &&
                    createPortal(
                        <div
                            style={{top: coords.top, left: coords.center}}
                            className='fixed -translate-x-1/2 -translate-y-full mb-2 flex flex-col gap-1 bg-white border border-divider rounded-[6px] shadow-card_menu px-3 py-2 z-[9999] whitespace-nowrap'
                        >
                            <div className='flex items-center gap-2 text-12 leading-normal'>
                                <div className='w-2 h-2 rounded-full bg-green-status shrink-0' />
                                <span className='text-santa font-normal'>Успішно:</span>
                                <span className='font-medium text-charcoal ml-auto'>{roundedSuccess}%</span>
                            </div>
                            <div className='flex items-center gap-2 text-12 leading-normal'>
                                <div className='w-2 h-2 rounded-full bg-red-status shrink-0' />
                                <span className='text-santa font-normal'>Помилка:</span>
                                <span className='font-medium text-charcoal ml-auto'>{roundedFailure}%</span>
                            </div>
                            <div className='flex items-center gap-2 text-12 leading-normal'>
                                <div className='w-2 h-2 rounded-full bg-grey-status shrink-0' />
                                <span className='text-santa font-normal'>Відсутні:</span>
                                <span className='font-medium text-charcoal ml-auto'>{absent}%</span>
                            </div>
                        </div>,
                        document.body
                    )}
            </div>
            {mobile && (
                <dl className='map-building-mobile-coverage-legend'>
                    {legend.map(({key, label, value, color}) => (
                        <div key={key}>
                            <dt>
                                <span aria-hidden style={{background: color}} />
                                {localizedStrings[label]}
                            </dt>
                            <dd>{value}%</dd>
                        </div>
                    ))}
                </dl>
            )}
        </section>
    );
};

export default DrawerSectionCoverageProgress;
